import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import adminOpsService from '../../services/adminOpsService';
import {
  Database,
  Upload,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Trash2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';

function safeErrorMessage(error) {
  const message = String(error?.message || error || '').trim();
  if (!message) return 'Unable to update the location catalog.';
  const lowered = message.toLowerCase();
  if (lowered.includes('traceback') || lowered.includes('password') || lowered.includes('secret')) {
    return 'Unable to update the location catalog.';
  }
  return message;
}

function formatImportedAt(value) {
  if (!value) return 'Not imported';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not imported';
  return date.toLocaleString();
}

const DataImpulseLocationCatalogPage = () => {
  const { getAuthHeaders } = useAuth();
  const fileInputRef = useRef(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const loadStatus = async () => {
    setLoading(true);
    setError('');
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      const payload = await adminOpsService.getDataimpulseLocationCatalog();
      setStatus(payload);
    } catch (err) {
      const message = safeErrorMessage(err);
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const imported = Boolean(status?.imported);
  const catalogs = status?.catalogs || {};

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setBusy(true);
    setError('');
    setConfirmDelete(false);
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      const payload = await adminOpsService.importDataimpulseLocationCatalog(file);
      setStatus(payload);
      toast.success(imported ? 'Catalog replaced' : 'Catalog imported');
    } catch (err) {
      const message = safeErrorMessage(err);
      setError(message);
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    setBusy(true);
    setError('');
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      const payload = await adminOpsService.deleteDataimpulseLocationCatalog();
      setStatus(payload);
      setConfirmDelete(false);
      toast.success('Catalog deleted');
    } catch (err) {
      const message = safeErrorMessage(err);
      setError(message);
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="dataimpulse-location-catalog-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Database className="text-amber-400" size={28} />
            DataImpulse Location Catalog
          </h1>
          <p className="text-slate-500 mt-1">
            Shared State, City, and ZIP catalog for every QA session.
          </p>
        </div>
        <Button variant="outline" onClick={loadStatus} disabled={loading || busy}>
          <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {error ? (
        <div
          className="bg-red-500/10 border border-red-500/30 rounded-xl p-4"
          data-testid="dataimpulse-catalog-error"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-red-400 flex-shrink-0 mt-0.5" size={20} />
            <p className="text-sm text-red-200">{error}</p>
          </div>
        </div>
      ) : null}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, index) => <Skeleton key={index} className="h-28 bg-stone-800" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
            <p className="text-sm text-slate-500 mb-1">Catalog status</p>
            <p
              className={`text-3xl font-bold ${imported ? 'text-emerald-400' : 'text-amber-400'}`}
              data-testid="dataimpulse-catalog-status"
            >
              {imported ? 'Ready' : 'Not imported'}
            </p>
          </div>
          <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
            <p className="text-sm text-slate-500 mb-1">Last import</p>
            <p className="text-lg font-semibold text-white" data-testid="dataimpulse-catalog-imported-at">
              {formatImportedAt(status?.importedAt)}
            </p>
          </div>
          <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
            <p className="text-sm text-slate-500 mb-1">State count</p>
            <p className="text-3xl font-bold text-white" data-testid="dataimpulse-catalog-state-count">
              {catalogs.state?.rowCount || 0}
            </p>
          </div>
          <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
            <p className="text-sm text-slate-500 mb-1">City count</p>
            <p className="text-3xl font-bold text-white" data-testid="dataimpulse-catalog-city-count">
              {catalogs.city?.rowCount || 0}
            </p>
          </div>
          <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
            <p className="text-sm text-slate-500 mb-1">ZIP count</p>
            <p className="text-3xl font-bold text-white" data-testid="dataimpulse-catalog-zip-count">
              {catalogs.zip?.rowCount || 0}
            </p>
          </div>
        </div>
      )}

      <div className="bg-stone-900 border border-stone-800 rounded-xl p-5 space-y-4">
        <div className="flex items-center gap-3">
          {imported ? (
            <CheckCircle className="text-emerald-400" size={20} />
          ) : (
            <AlertTriangle className="text-amber-400" size={20} />
          )}
          <p className="text-sm text-slate-400">
            {imported
              ? 'QA sessions can search this shared catalog.'
              : 'QA location search is unavailable until a workbook is imported.'}
          </p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="hidden"
          data-testid="dataimpulse-catalog-file"
          onChange={handleUpload}
        />
        <div className="flex flex-wrap gap-3">
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
            data-testid="dataimpulse-catalog-upload"
          >
            <Upload size={16} className="mr-2" />
            {busy ? 'Uploading…' : imported ? 'Replace catalog' : 'Upload workbook'}
          </Button>
          {imported ? (
            <Button
              variant="outline"
              onClick={() => setConfirmDelete(true)}
              disabled={busy}
              data-testid="dataimpulse-catalog-delete"
            >
              <Trash2 size={16} className="mr-2" />
              Delete catalog
            </Button>
          ) : null}
        </div>
        {confirmDelete ? (
          <div
            className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 space-y-3"
            data-testid="dataimpulse-catalog-delete-confirm"
          >
            <p className="text-sm text-red-200">
              Delete this catalog? This will disable QA location search for everyone.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={busy}
                data-testid="dataimpulse-catalog-delete-confirm-button"
              >
                Delete catalog
              </Button>
              <Button
                variant="outline"
                onClick={() => setConfirmDelete(false)}
                disabled={busy}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default DataImpulseLocationCatalogPage;
